/**
 * Everything that happens without anybody pressing a key.
 *
 * Three clocks run on a container, and they are deliberately independent —
 * Yiheng's meeting note, "2 timers, 1 for burning, 1 for fire after burnt",
 * plus the design doc's time constraint #4:
 *
 *   cooking --toDone--> done --toBurnt--> burnt --toFire--> the equipment alight
 *
 * All three durations come from `timings.json` by way of the `cookable` row
 * that produced the food, which is what makes them the difficulty dials the
 * design doc says they are.
 *
 * Every one of them only advances while the container is standing on cooking
 * equipment, because the clocks live on the container's phase stamps and those
 * stamps are only ever set by the equipment. Carry a pan away and it stops.
 *
 * Alongside them run the held clocks — a chop on the board, a wash at the sink,
 * and now a fire being put out — which only advance while somebody holds the
 * key, and the plate return, which brings a served plate back dirty.
 *
 * None of these count. Each is stored as the tick it is due, so a twelve-second
 * cook is two state changes rather than six hundred, and a replay that folds
 * those two events lands on exactly the state the simulation was in.
 */

import type { GameEvent, IngredientId, Station } from '@overcooked/shared'
import { cellKey, isCookingEquipment } from '@overcooked/shared'
import type { Content } from './content'
import { SINK_PILE_SUBJECT, setContainer } from './interact'
import {
  cookableFor,
  durationTicks,
  extinguishTicks,
  fireTicks,
  phaseTicksFor,
  platesPerMap,
  transformFor,
  washTicks,
} from './rules'
import type { GameState } from './state'
import { stationOrder } from './stationOrder'

export function advanceTimers(state: GameState, events: GameEvent[], content: Content): void {
  // Sorted so the event order for a given state is the same on every run,
  // whatever order the stations happened to be inserted in.
  for (const key of stationOrder(state)) {
    const station = state.stations[key]
    advanceHeldProgress(state, station, events, content)
    advanceCooking(state, station, events, content)
  }
  returnPlates(state, events, content)
}

// ---------------------------------------------------------------------------
// Held progress: the board and the sink
// ---------------------------------------------------------------------------

function advanceHeldProgress(
  state: GameState,
  station: Station,
  events: GameEvent[],
  content: Content,
): void {
  if (station.resumedAtTick === null) return
  const elapsed = station.progressTicks + (state.tick - station.resumedAtTick)

  // Putting a fire out. Held, like a chop, so a fire costs a player for a few
  // seconds rather than costing a keypress — that is what makes it a real
  // interruption to a two-person kitchen.
  if (station.onFire) {
    const holder = station.heldBy ? state.players[station.heldBy] : undefined
    const carrying = holder?.holding
    if (!holder || !carrying || carrying.type !== 'equipment' || carrying.id !== 'extinguisher') {
      clearHold(station)
      return
    }
    if (elapsed < extinguishTicks(content)) return

    const actor = holder.id
    station.onFire = false
    clearHold(station)
    events.push({
      tick: state.tick,
      wallClock: 0,
      actor,
      type: 'FIRE_EXTINGUISHED',
      playerId: actor,
      cell: { ...station.cell },
    })
    // And that is the whole of it. Nothing else moves.
    //
    // Both of the things that used to happen here were the same mistake wearing
    // two hats: the extinguisher flew back to a rack nobody had walked to, and
    // the burnt food vanished out of the pan with `playerId: 'system'`. A
    // playtester reported both as bugs, in those words, and they were right —
    // an object that changes place with no one carrying it is the one thing a
    // kitchen full of physical objects must never do.
    //
    // So the extinguisher stays in the hands that were holding it, and somebody
    // walks it back to its rack; and the pan is left burnt, with no fire clock
    // under it, to be lifted and carried to the bin like any other burnt pan.
    // One rule for burnt food everywhere, and no second vanishing act.
    return
  }

  if (station.type === 'board') {
    const item = station.item
    if (!item || item.type !== 'ingredient') return
    const transform = transformFor(content, 'board', item.id)
    if (!transform) return
    if (elapsed < durationTicks(content, transform.durationKey)) return

    const actor = station.heldBy
    if (!actor) return
    station.item = { type: 'ingredient', id: transform.to }
    clearHold(station)
    events.push({
      tick: state.tick,
      wallClock: 0,
      actor,
      type: 'CHOPPED',
      playerId: actor,
      cell: { ...station.cell },
      from: transform.from,
      to: transform.to,
    })
    return
  }

  if (station.type === 'sink') {
    const washer = station.heldBy ? state.players[station.heldBy] : undefined
    const holding = washer?.holding

    // Washing the top plate of the pile: the hands stay empty until it is
    // clean, then the plate comes off the pile into them.
    if (station.heldSubject === SINK_PILE_SUBJECT) {
      if (!washer || holding || station.dirtyPlates <= 0) {
        clearHold(station)
        return
      }
      if (elapsed < washTicks(content)) return

      // Derived from the cell and the remaining count, exactly as the plate
      // return and the clean stack derive theirs.
      const plateId = `plate-${station.cell.x}-${station.cell.y}-${station.dirtyPlates}`
      station.dirtyPlates -= 1
      washer.holding = { type: 'plate', plate: { id: plateId, clean: true, contents: [] } }
      const actor = washer.id
      clearHold(station)
      events.push({
        tick: state.tick,
        wallClock: 0,
        actor,
        type: 'WASHED',
        playerId: actor,
        cell: { ...station.cell },
        plateId,
      })
      return
    }

    // The plate stays in the washer's hands; if it is no longer there the hold
    // is meaningless and the progress simply freezes where it is.
    if (!washer || !holding || holding.type !== 'plate' || holding.plate.clean) {
      clearHold(station)
      return
    }
    if (elapsed < washTicks(content)) return

    holding.plate.clean = true
    const plateId = holding.plate.id
    const actor = washer.id
    clearHold(station)
    events.push({
      tick: state.tick,
      wallClock: 0,
      actor,
      type: 'WASHED',
      playerId: actor,
      cell: { ...station.cell },
      plateId,
    })
  }
}

function clearHold(station: Station): void {
  station.progressTicks = 0
  station.progressTargetTicks = 0
  station.resumedAtTick = null
  station.heldBy = null
  station.heldSubject = null
}

// ---------------------------------------------------------------------------
// The pan
// ---------------------------------------------------------------------------

function advanceCooking(state: GameState, station: Station, events: GameEvent[], content: Content): void {
  // The clock is the equipment's, not the container's: a pan carried away from
  // the heat carries no phase stamps, so nothing here can advance it.
  if (!isCookingEquipment(station.type)) return
  if (station.onFire) return
  const container = station.container
  if (!container || container.phaseEndsAtTick === null) return
  if (state.tick < container.phaseEndsAtTick) return

  if (container.cookState === 'cooking') {
    // Reaching `done` is where the transformation actually happens — chopped
    // meat becomes fried meat, chopped carrot becomes stewed carrot — and the
    // second clock starts.
    const cooked: IngredientId[] = container.contents.map((id) => {
      const cookable = cookableFor(content, station.type, container.kind, id)
      return cookable ? cookable.to : id
    })
    const toBurnt = phaseTicksFor(content, station.type, container, 'toBurntKey', container.contents)
    const burnAt = state.tick + (toBurnt ?? durationTicks(content, 'doneToBurntMs'))
    setContainer(state, station, 'done', cooked, state.tick, burnAt, events)
    return
  }

  if (container.cookState === 'done') {
    // Time constraint #3 into #4: burnt is no longer terminal. The third clock
    // starts here, and if nobody empties the container before it runs out the
    // equipment itself catches.
    const fireAt = state.tick + fireTicks(content, station.type, container)
    setContainer(state, station, 'burnt', container.contents, state.tick, fireAt, events)
    return
  }

  if (container.cookState === 'burnt') {
    // Time constraint #4. The station is now inert to everything except
    // somebody arriving with the extinguisher.
    station.onFire = true
    station.progressTicks = 0
    station.progressTargetTicks = 0
    station.resumedAtTick = null
    station.heldBy = null
    station.heldSubject = null
    events.push({
      tick: state.tick,
      wallClock: 0,
      actor: 'system',
      type: 'FIRE_STARTED',
      cell: { ...station.cell },
    })
    setContainer(state, station, 'burnt', container.contents, null, null, events)
  }
}

// ---------------------------------------------------------------------------
// The plate return
// ---------------------------------------------------------------------------

/**
 * A served plate reappears dirty after `plateReturnDelayMs`, with no warning
 * and no visible timer.
 *
 * The return stacks up to `platesPerMap` of them, which is every plate the
 * kitchen owns, so the return itself can never be the reason a plate is stuck
 * in the queue. "Three plates and all of them dirty" stays a real pressure —
 * it is now a pressure on the sink rather than a pressure on one shelf.
 */
function returnPlates(state: GameState, events: GameEvent[], content: Content): void {
  if (state.pendingPlates.length === 0) return

  const returns = Object.values(state.stations)
    .filter((s) => s.type === 'plate_return')
    // Codepoint order, never `localeCompare`: ICU collation depends on the
    // host's locale data, and this is a reducer whose whole contract is that
    // two machines folding the same log reach the same state. One node build
    // without full-icu, or one box on a different locale, and two plate returns
    // could take the plates in opposite orders.
    .sort((a, b) => (cellKey(a.cell) < cellKey(b.cell) ? -1 : cellKey(a.cell) > cellKey(b.cell) ? 1 : 0))
  if (returns.length === 0) return

  const capacity = platesPerMap(content)
  const stillPending: typeof state.pendingPlates = []
  for (const pending of state.pendingPlates) {
    if (pending.dueAtTick > state.tick) {
      stillPending.push(pending)
      continue
    }
    const free = returns.find((s) => s.dirtyPlates < capacity)
    if (!free) {
      stillPending.push(pending)
      continue
    }
    free.dirtyPlates += 1
    events.push({
      tick: state.tick,
      wallClock: 0,
      actor: 'system',
      type: 'PLATE_RETURNED',
      cell: { ...free.cell },
      plateId: pending.plateId,
    })
  }

  state.pendingPlates = stillPending
}
